import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { AUTH_CONFIG } from '@/config/auth';
import { AuthContext } from './useAuthContext';

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  phone?: string;
  avatarUrl?: string;
  isAgency: boolean;
  practiceId?: string;
  role?: string;
  locationIds?: string[];
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Restore user from localStorage
    const storedUser = localStorage.getItem(AUTH_CONFIG.userKey);
    const accessToken = localStorage.getItem(AUTH_CONFIG.accessTokenKey);

    if (storedUser && accessToken) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem(AUTH_CONFIG.userKey);
      }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback((accessToken: string, refreshToken: string, userData: AuthUser) => {
    localStorage.setItem(AUTH_CONFIG.accessTokenKey, accessToken);
    localStorage.setItem(AUTH_CONFIG.refreshTokenKey, refreshToken);
    localStorage.setItem(AUTH_CONFIG.userKey, JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_CONFIG.accessTokenKey);
    localStorage.removeItem(AUTH_CONFIG.refreshTokenKey);
    localStorage.removeItem(AUTH_CONFIG.userKey);
    setUser(null);
  }, []);

  const updateUser = useCallback((updates: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem(AUTH_CONFIG.userKey, JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
